<template>
  <Dialog
    :visible="visible"
    modal
    :closable="false"
    :style="{ width: '52rem' }"
    @update:visible="$emit('update:visible', $event)">
    <template #header>
      <div class="flex w-full items-center justify-between">
        <div>
          <h3 class="text-xl font-semibold">Start your free trial to go live</h3>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Choose a plan and your changes will be published immediately.
          </p>
          <p v-if="selectedPlanRecommendationCopy" class="text-brand-text mt-2 text-sm dark:text-gray-300">
            {{ selectedPlanRecommendationCopy }}
          </p>
        </div>
        <Button severity="secondary" label="Cancel" outlined @click="$emit('update:visible', false)" />
      </div>
    </template>

    <div class="space-y-6">
      <!-- Monthly/Annual Toggle -->
      <div class="flex justify-center">
        <div class="inline-flex items-center gap-3 rounded-full bg-gray-100 p-1 dark:bg-slate-800">
          <button
            :class="[
              'rounded-full px-4 py-2 text-sm font-semibold transition-all',
              !isAnnual
                ? 'bg-white text-gray-900 shadow dark:bg-slate-700 dark:text-white'
                : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
            ]"
            @click="isAnnual = false">
            Monthly
          </button>
          <button
            :class="[
              'rounded-full px-4 py-2 text-sm font-semibold transition-all',
              isAnnual
                ? 'bg-white text-gray-900 shadow dark:bg-slate-700 dark:text-white'
                : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
            ]"
            @click="isAnnual = true">
            Annual
            <span class="ml-1 text-xs font-medium text-green-600 dark:text-green-400">Save 20%</span>
          </button>
        </div>
      </div>

      <p class="text-center text-sm text-gray-500 dark:text-gray-400">
        Annual is selected by default; switch to Monthly if you prefer month-to-month billing.
      </p>

      <!-- Plan Cards -->
      <div class="grid grid-cols-3 gap-4">
        <button
          v-for="plan in displayPlans"
          :key="plan.tier"
          type="button"
          :class="[
            'relative rounded-2xl bg-white p-5 text-left shadow-sm transition-all dark:bg-slate-800',
            selectedPlan === plan.tier
              ? 'ring-2 ring-indigo-500 dark:ring-indigo-400'
              : 'ring-1 ring-gray-200 hover:ring-gray-300 dark:ring-slate-700 dark:hover:ring-slate-600'
          ]"
          @click="selectPlan(plan.tier)">
          <!-- Recommended badge (based on what they've built) -->
          <div
            v-if="plan.tier === minimumRequiredTier && minimumRequiredTier !== 'grow'"
            class="absolute -top-3 left-1/2 -translate-x-1/2">
            <span
              class="inline-flex items-center rounded-full bg-indigo-500 px-3 py-1 text-xs font-semibold text-white">
              Recommended
            </span>
          </div>
          <!-- Most Popular badge (only when no specific recommendation) -->
          <div
            v-else-if="plan.display?.popular && minimumRequiredTier === 'grow'"
            class="absolute -top-3 left-1/2 -translate-x-1/2">
            <span
              class="inline-flex items-center rounded-full bg-indigo-500 px-3 py-1 text-xs font-semibold text-white">
              Most Popular
            </span>
          </div>

          <div class="text-base font-semibold text-gray-900 dark:text-white">
            {{ plan.name }}
          </div>
          <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {{ plan.display?.tagline }}
          </p>
          <div class="mt-4 flex items-baseline gap-x-1">
            <span class="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
              {{ isAnnual ? plan.display?.yearly_price : plan.display?.monthly_price }}
            </span>
            <span class="text-sm text-gray-500 dark:text-gray-400">/mo</span>
          </div>
          <p v-if="isAnnual" class="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Billed annually ({{ plan.display?.yearly_total }}/yr)
          </p>

          <!-- Feature list -->
          <ul class="mt-4 space-y-1.5 text-xs">
            <li
              v-for="feature in plan.display?.features ?? []"
              :key="feature"
              class="flex items-start gap-x-1.5 text-gray-600 dark:text-gray-300">
              <CheckIcon class="mt-0.5 h-3 w-3 flex-none text-indigo-500" />
              {{ feature }}
            </li>
          </ul>

          <!-- Selected indicator -->
          <div v-if="selectedPlan === plan.tier" class="absolute right-3 top-3">
            <div class="flex h-5 w-5 items-center justify-center rounded-full bg-indigo-500">
              <i class="pi pi-check text-xs text-white" />
            </div>
          </div>
        </button>
      </div>

      <div v-if="displayPlans.length === 0" class="rounded-lg bg-red-50 px-4 py-3 dark:bg-red-900/20">
        <p class="text-sm text-red-700 dark:text-red-300">
          {{ emptyPlansError }}
        </p>
      </div>

      <!-- Insufficient plan warning -->
      <div v-if="selectedPlanInsufficient" class="rounded-lg bg-amber-50 px-4 py-3 dark:bg-amber-900/20">
        <div class="flex items-start gap-2">
          <i class="pi pi-exclamation-triangle mt-0.5 text-sm text-amber-600 dark:text-amber-400" />
          <div>
            <span class="text-sm font-medium text-amber-800 dark:text-amber-200">
              Your setup requires {{ minimumRequiredTierName }} or higher
            </span>
            <p class="mt-0.5 text-xs text-amber-700 dark:text-amber-300">
              You're using {{ requiredFeatureReasons.join(' and ') }}, which
              {{ requiredFeatureReasons.length > 1 ? 'are' : 'is' }} not included in the selected plan. You can still
              choose this plan, but those features won't be available.
            </p>
          </div>
        </div>
      </div>

      <!-- Card Input -->
      <div class="space-y-3">
        <div class="flex items-center justify-between">
          <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Payment Information</label>
          <span class="text-xs text-gray-500 dark:text-gray-400">
            <i class="pi pi-lock mr-1" />
            Secure payment via Stripe
          </span>
        </div>
        <div class="rounded-lg border bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
          <div id="publish-subscribe-card-element" class="min-h-[24px]">
            <!-- Stripe card element mounts here -->
          </div>
        </div>
        <div v-if="stripeError" class="text-sm text-red-600 dark:text-red-400">
          {{ stripeError }}
        </div>
      </div>

      <!-- Trial messaging -->
      <div class="rounded-lg bg-green-50 px-4 py-3 dark:bg-green-900/20">
        <div class="flex items-center gap-2">
          <i class="pi pi-shield text-green-600 dark:text-green-400" />
          <span class="text-sm font-medium text-green-800 dark:text-green-200">
            Your card won't be charged for 14 days
          </span>
        </div>
        <p class="ml-6 mt-1 text-xs text-green-700 dark:text-green-300">
          Cancel anytime before it ends and you won't be charged. You can upgrade your plan at any time.
        </p>
      </div>

      <!-- Error display -->
      <div v-if="submitError" class="rounded-lg bg-red-50 px-4 py-3 dark:bg-red-900/20">
        <p class="text-sm text-red-700 dark:text-red-300">
          {{ submitError }}
        </p>
      </div>

      <p v-if="showCardDetailsHelper" class="text-brand-text text-sm dark:text-gray-300">
        Enter card details to start the trial and publish.
      </p>

      <!-- Submit button -->
      <button
        :disabled="isSubmitDisabled"
        class="w-full rounded-lg bg-indigo-600 px-4 py-3 text-center text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
        @click="handleSubscribeAndPublish">
        <i v-if="isSubmitting" class="pi pi-spin pi-spinner mr-2" />
        <i v-else class="pi pi-bolt mr-2" />
        {{ isSubmitting ? 'Setting up your trial...' : 'Start 14-day trial & publish' }}
      </button>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { loadStripe } from '@stripe/stripe-js'
import type { Stripe, StripeElements, StripeCardElement } from '@stripe/stripe-js'
import { CheckIcon } from '@heroicons/vue/20/solid'
import api from '@/services/axiosApi'
import { AnalyticsEventTypes, recordAnalyticsEvent } from '@/services/analytics'
import {
  clearActivationWizardPublishCorrelation,
  getPendingActivationWizardPublishCorrelation
} from '@/modules/onboarding/services/activationWizardTelemetry'

type PublishPlan = {
  tier: string
  name: string
  monthly_price_id: string | null
  yearly_price_id: string | null
  tagline?: string
  popular?: boolean
  features?: string[]
  monthlyPrice?: string
  annualPrice?: string
  annualTotal?: string
  display?: {
    monthly_price?: string | null
    yearly_price?: string | null
    yearly_total?: string | null
    tagline?: string | null
    popular?: boolean | null
    features?: string[] | null
  } | null
}
type WizardCorrelation = ReturnType<typeof getPendingActivationWizardPublishCorrelation>

type PendingAuthenticatedPayment = {
  paymentIntentId: string
  priceId: string
  wizardCorrelation: WizardCorrelation
}

const props = defineProps<{
  visible: boolean
  publishMessage?: string | null
  plans?: PublishPlan[]
}>()

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'subscribed'): void
}>()

const route = useRoute()
const notificationStore = useNotificationStore()
const configStore = useConfigStore()
const { config, currentTeam, lastChecksum } = storeToRefs(configStore)
const integrationStore = useIntegrationStore()
const { integratedPartnerName } = storeToRefs(integrationStore)

const isAnnual = ref(true)
const selectedPlan = ref('')
const stripeError = ref('')
const submitError = ref('')
const isSubmitting = ref(false)
const stripeReady = ref(false)
const autoSelectedPlan = ref('')
const pendingAuthenticatedPayment = ref<PendingAuthenticatedPayment | null>(null)

// Stripe elements
let stripe: Stripe | null = null
let elements: StripeElements | null = null
let cardElement: StripeCardElement | null = null
let stripeInitialized = false

// ESP providers grouped by minimum tier
const scaleEsps = ['klaviyo', 'customerio', 'gohighlevel', 'mailchimp', 'infusionsoft', 'ontraport', 'userlist']
const proEsps = ['hubspot', 'braze', 'pardot', 'intercom']

// Check if any flow contains a webhook node (uses existing funnelDefinitionEach traversal)
const hasWebhooksInFlows = computed(() => {
  const funnels = config.value?.rightCta?.offerFunnels ?? []
  for (const funnel of funnels) {
    if (!funnel.funnelDefinition) continue
    let found = false
    funnelDefinitionEach(funnel.funnelDefinition, (node: any) => {
      if (node.type === 'webhook') found = true
    })
    if (found) return true
  }
  return false
})

// Detect what the user has actually built to determine minimum required tier
const minimumRequiredTier = computed<'grow' | 'scale' | 'pro'>(() => {
  // Check ESP first — most restrictive
  const esp = integratedPartnerName.value
  if (esp && proEsps.includes(esp)) return 'pro'
  if (esp && scaleEsps.includes(esp)) return 'scale'

  // Check for Pro-only features
  if (hasWebhooksInFlows.value) return 'pro'

  // Check for Scale-only features
  const hasCampaigns = (config.value?.campaigns?.length ?? 0) > 0

  if (hasCampaigns) return 'scale'

  return 'grow'
})

// Reasons the user needs a specific tier (for the warning message)
const requiredFeatureReasons = computed<string[]>(() => {
  const reasons: string[] = []
  const esp = integratedPartnerName.value

  if (esp && proEsps.includes(esp)) {
    const label = espLabels[esp] || esp
    reasons.push(`${label} integration`)
  } else if (esp && scaleEsps.includes(esp)) {
    const label = espLabels[esp] || esp
    reasons.push(`${label} integration`)
  }

  if (hasWebhooksInFlows.value) {
    reasons.push('webhook nodes in your flows')
  }

  if ((config.value?.campaigns?.length ?? 0) > 0) {
    reasons.push('personalization campaigns')
  }

  return reasons
})

const espLabels: Record<string, string> = {
  hubspot: 'HubSpot',
  braze: 'Braze',
  pardot: 'Salesforce/Pardot',
  intercom: 'Intercom',
  klaviyo: 'Klaviyo',
  customerio: 'Customer.io',
  gohighlevel: 'GoHighLevel',
  mailchimp: 'Mailchimp',
  infusionsoft: 'Keap',
  ontraport: 'Ontraport',
  userlist: 'Userlist'
}

const tierOrder = ['grow', 'scale', 'pro'] as const
const emptyPlansError = 'Plan details could not be loaded. Close this window and try publishing again.'

const tierSortIndex = (tier: string) => {
  const index = tierOrder.indexOf(tier as (typeof tierOrder)[number])
  return index === -1 ? tierOrder.length : index
}

const displayPlans = computed(() =>
  [...(props.plans ?? [])].sort((left, right) => {
    return tierSortIndex(left.tier) - tierSortIndex(right.tier)
  })
)

const selectedPlanRecord = computed(() => displayPlans.value.find((entry) => entry.tier === selectedPlan.value) ?? null)

const planTagline = (plan: PublishPlan | null) => {
  if (!plan) return ''
  return plan.display?.tagline ?? plan.tagline ?? ''
}

const selectedPlanRecommendationCopy = computed(() => {
  if (!selectedPlanRecord.value || !autoSelectedPlan.value || selectedPlan.value !== autoSelectedPlan.value) {
    return ''
  }

  const planName = selectedPlanRecord.value.name
  const reason =
    selectedPlanRecord.value.tier === 'scale'
      ? 'it includes website personalization and conversion goals.'
      : `${planTagline(selectedPlanRecord.value).replace(/\.*$/, '') || 'it best fits the setup you just built'}.`

  return `We recommend ${planName} for this setup because ${reason} You can choose another plan before starting the trial.`
})

const showCardDetailsHelper = computed(() => {
  return !stripeReady.value && !isSubmitting.value && displayPlans.value.length > 0
})

const selectPlan = (tier: string) => {
  selectedPlan.value = tier
  autoSelectedPlan.value = ''
}

const isSubmitDisabled = computed(() => isSubmitting.value || !stripeReady.value || displayPlans.value.length === 0)

// Whether the currently selected plan is insufficient for what they've built
const selectedPlanInsufficient = computed(() => {
  const selectedIndex = tierSortIndex(selectedPlan.value)
  const requiredIndex = tierSortIndex(minimumRequiredTier.value)
  return selectedPlanRecord.value !== null && selectedIndex < requiredIndex
})

// Human-readable name for the minimum required tier
const minimumRequiredTierName = computed(() => {
  const names: Record<string, string> = { grow: 'Grow', scale: 'Scale', pro: 'Pro' }
  return names[minimumRequiredTier.value]
})

// Auto-select recommended plan based on what user has built
watch(
  () => props.visible,
  (newVal) => {
    if (newVal) {
      // Pre-select the minimum tier that covers their usage (at least Scale as default)
      const requiredIndex = tierSortIndex(minimumRequiredTier.value)
      const scaleIndex = tierSortIndex('scale')
      const recommendedTier = tierOrder[Math.max(requiredIndex, scaleIndex)] ?? 'scale'
      selectedPlan.value =
        displayPlans.value.find((entry) => entry.tier === recommendedTier)?.tier ?? displayPlans.value[0]?.tier ?? ''
      autoSelectedPlan.value = selectedPlan.value
    }
  }
)

const initializeStripe = async () => {
  if (stripeInitialized) return

  try {
    const { data } = await api.get('/api/stripe/publishable-key')
    stripe = await loadStripe(data.key)

    if (!stripe) {
      throw new Error('Failed to load Stripe')
    }

    const isDarkMode = document.documentElement.classList.contains('dark')

    elements = stripe.elements()
    cardElement = elements.create('card', {
      style: {
        base: {
          fontSize: '16px',
          color: isDarkMode ? '#e5e7eb' : '#111827',
          '::placeholder': {
            color: isDarkMode ? '#9ca3af' : '#6b7280'
          }
        },
        invalid: {
          color: isDarkMode ? '#fca5a5' : '#fa755a'
        }
      }
    })

    stripeInitialized = true
    await mountCardElement()
  } catch (error) {
    console.error('Failed to initialize Stripe:', error)
    submitError.value = 'Failed to load payment form. Please try again.'
  }
}

const mountCardElement = async () => {
  if (!cardElement) return

  await nextTick()

  let attempts = 0
  const maxAttempts = 10

  const tryMount = () => {
    const el = document.getElementById('publish-subscribe-card-element')
    if (el && cardElement) {
      cardElement.mount(el)
      cardElement.on('change', (event) => {
        stripeError.value = event.error?.message || ''
        stripeReady.value = event.complete
      })
      return true
    }
    return false
  }

  if (tryMount()) return

  const interval = setInterval(() => {
    attempts++
    if (tryMount() || attempts >= maxAttempts) {
      clearInterval(interval)
    }
  }, 100)
}

const submitSubscribeAndPublish = async (
  paymentMethodId: string,
  priceId: string,
  wizardCorrelation: WizardCorrelation
) => {
  const teamPid = route.params.teamPid
  const response = await api.post(`/api/${teamPid}/billing/subscribe-and-publish`, {
    payment_method_id: paymentMethodId,
    price_id: priceId,
    data: toRaw(config.value),
    checksum: lastChecksum.value,
    activation_wizard_correlation: wizardCorrelation || undefined,
    publish_message: props.publishMessage || undefined
  })

  return response.data as { subscription_created?: boolean }
}
const completeSubscribeAndPublish = async (
  paymentIntentId: string,
  priceId: string,
  wizardCorrelation: WizardCorrelation
) => {
  const teamPid = route.params.teamPid
  const response = await api.post(`/api/${teamPid}/billing/subscribe-and-publish`, {
    payment_intent_id: paymentIntentId,
    price_id: priceId,
    data: toRaw(config.value),
    checksum: lastChecksum.value,
    activation_wizard_correlation: wizardCorrelation || undefined,
    publish_message: props.publishMessage || undefined
  })

  return response.data as { subscription_created?: boolean }
}

const finishSubscribedAndPublished = async (options?: {
  subscriptionCreated?: boolean
  wizardCorrelation?: WizardCorrelation
}) => {
  const notificationMessage =
    options?.subscriptionCreated === false
      ? 'Your changes are now live!'
      : 'Your trial has started and your changes are now live!'

  await configStore.configQuery.refetch()
  recordAnalyticsEvent(AnalyticsEventTypes.PUBLISHED)
  if (!currentTeam.value?.last_published_at) {
    recordAnalyticsEvent(AnalyticsEventTypes.FIRST_PUBLISH)
  }

  const activationWizardPublishCorrelation =
    options?.wizardCorrelation || getPendingActivationWizardPublishCorrelation()
  if (activationWizardPublishCorrelation) {
    recordAnalyticsEvent(
      AnalyticsEventTypes.ACTIVATION_WIZARD_POST_BUILD_PUBLISH_SUCCEEDED,
      activationWizardPublishCorrelation
    )
    clearActivationWizardPublishCorrelation()
  }

  notificationStore.addNotification(notificationMessage)

  emit('subscribed')
  emit('update:visible', false)
}

const subscribeErrorMessage = (error: any) =>
  error.response?.data?.error || error.response?.data?.message || 'Failed to start trial. Please try again.'

const handleSubscribeAndPublish = async () => {
  if (!stripe || !cardElement) {
    submitError.value = 'Payment form not ready. Please wait a moment and try again.'
    return
  }

  isSubmitting.value = true
  submitError.value = ''

  let paymentMethodId: string | null = null
  let priceId: string | null = null
  const wizardCorrelation = getPendingActivationWizardPublishCorrelation()

  if (pendingAuthenticatedPayment.value) {
    try {
      const pendingPayment = pendingAuthenticatedPayment.value
      const subscribeResult = await completeSubscribeAndPublish(
        pendingPayment.paymentIntentId,
        pendingPayment.priceId,
        pendingPayment.wizardCorrelation
      )
      await finishSubscribedAndPublished({
        subscriptionCreated: subscribeResult.subscription_created !== false,
        wizardCorrelation: pendingPayment.wizardCorrelation
      })
      pendingAuthenticatedPayment.value = null
    } catch (error: any) {
      submitError.value = subscribeErrorMessage(error)
    } finally {
      isSubmitting.value = false
    }
    return
  }

  try {
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement
    })

    if (error) {
      stripeError.value = error.message || 'Payment method creation failed'
      return
    }

    if (!paymentMethod?.id) {
      submitError.value = 'Payment method creation failed. Please try another card.'
      return
    }

    paymentMethodId = paymentMethod.id

    const plan = selectedPlanRecord.value
    priceId = plan ? (isAnnual.value ? plan.yearly_price_id : plan.monthly_price_id) : null

    if (!priceId) {
      submitError.value = 'Could not determine plan price. Please try again.'
      return
    }

    const subscribeResult = await submitSubscribeAndPublish(paymentMethodId, priceId, wizardCorrelation)
    await finishSubscribedAndPublished({
      subscriptionCreated: subscribeResult.subscription_created !== false,
      wizardCorrelation
    })
  } catch (error: any) {
    if (
      error.response?.status === 402 &&
      error.response?.data?.requires_action &&
      error.response?.data?.payment_intent_client_secret &&
      error.response?.data?.payment_intent_id &&
      paymentMethodId &&
      priceId
    ) {
      if (!stripe) {
        submitError.value =
          'Your bank requires extra authentication, but the payment form is not ready. Please reload and try again.'
        return
      }

      submitError.value = ''

      const confirmation = await stripe.confirmCardPayment(error.response.data.payment_intent_client_secret)
      if (confirmation.error) {
        submitError.value =
          confirmation.error.message ||
          'Your bank could not verify the card. Please try another card or contact your bank.'
        return
      }

      pendingAuthenticatedPayment.value = {
        paymentIntentId: error.response.data.payment_intent_id,
        priceId,
        wizardCorrelation
      }

      try {
        const subscribeResult = await completeSubscribeAndPublish(
          error.response.data.payment_intent_id,
          priceId,
          wizardCorrelation
        )
        await finishSubscribedAndPublished({
          subscriptionCreated: subscribeResult.subscription_created !== false,
          wizardCorrelation
        })
        pendingAuthenticatedPayment.value = null
      } catch (retryError: any) {
        submitError.value = subscribeErrorMessage(retryError)
      }
      return
    }

    submitError.value = subscribeErrorMessage(error)
  } finally {
    isSubmitting.value = false
  }
}

// Initialize Stripe when modal becomes visible
watch(
  () => props.visible,
  async (newVal) => {
    if (newVal) {
      submitError.value = ''
      stripeError.value = ''
      await initializeStripe()

      // Re-mount if already initialized (modal was closed and reopened)
      if (stripeInitialized && cardElement) {
        await nextTick()
        await mountCardElement()
      }
    }
  }
)

// Reset Stripe when modal closes so it re-initializes with current styles
watch(
  () => props.visible,
  (newVal) => {
    if (!newVal && cardElement) {
      cardElement.destroy()
      cardElement = null
      elements = null
      stripeInitialized = false
      stripeReady.value = false
    }
  }
)

onUnmounted(() => {
  if (cardElement) {
    cardElement.destroy()
  }
})
</script>
